import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiUrl, fmtFecha } from '../utils/api';
import { Spinner, Empty, ConfirmModal } from '../components/UI';
import NuevoClienteForm from '../components/NuevoClienteForm';

const ESTADOS = [
  { val: 'B', label: 'B' },
  { val: 'R', label: 'R' },
  { val: 'M', label: 'M' },
  { val: 'NA', label: 'N/A' },
];

// Campos del vehículo que se muestran y se pueden editar en la revisión
const CAMPOS_VEHICULO = [
  ['marca', 'Marca'], ['modelo', 'Modelo'], ['anio', 'Año'], ['color', 'Color'],
  ['combustible', 'Combustible'], ['transmision', 'Transmisión'],
  ['motor', 'Motor (cilindrada)'], ['admision', 'Admisión'],
  ['matricula', 'Matrícula'], ['n_chasis', 'N° de chasis (VIN)'], ['n_motor', 'N° de motor'],
];

function camposDeVehiculo(v) {
  const res = {};
  CAMPOS_VEHICULO.forEach(([k]) => { res[k] = v && v[k] != null ? String(v[k]) : ''; });
  return res;
}

function etiquetaVehiculo(v) {
  const base = [v.marca, v.modelo, v.anio ? `(${v.anio})` : ''].filter(Boolean).join(' ') || 'Vehículo sin datos';
  const mat = v.matricula ? ` — ${v.matricula}` : '';
  const estado = v.estado_venta === 'vendido' ? ' · vendido' : v.estado_venta === 'en_evaluacion' ? ' · en evaluación' : '';
  return `${base}${mat}${estado}`;
}

// ── Piezas compartidas ────────────────────────────────────────────

function CamposVehiculo({ veh, setVeh }) {
  return (
    <div className="form-grid-2" style={{ rowGap: '.5rem' }}>
      {CAMPOS_VEHICULO.map(([k, etiqueta]) => (
        <input key={k} className="form-input" placeholder={etiqueta} value={veh[k]}
          onChange={e => setVeh(v => ({ ...v, [k]: e.target.value }))} />
      ))}
    </div>
  );
}

function CargarFoto({ onItems }) {
  const [leyendo, setLeyendo] = useState(false);
  const leerFoto = async (file) => {
    if (!file) return;
    setLeyendo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(apiUrl('/revisiones/leer-foto'), { method: 'POST', body: formData });
      if (!res.ok) throw new Error();
      const data = await res.json();
      onItems(data.items);
    } catch {
      alert('No se pudo leer la foto. Probá con otra más clara, o cargá a mano.');
    }
    setLeyendo(false);
  };
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Cargar desde foto de la planilla (opcional)</p>
      <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginBottom: '.6rem' }}>
        Sacale una foto bien derecha y con buena luz a la hoja ya completada a mano. La IA va a precargar los casilleros — después revisá y corregí lo que haga falta.
      </p>
      <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
        {leyendo ? '... leyendo' : '📷 Cargar foto de la planilla'}
        <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
          disabled={leyendo} onChange={e => leerFoto(e.target.files[0])} />
      </label>
      {leyendo && <Spinner texto="Leyendo planilla con IA..." />}
    </div>
  );
}

function ListaItems({ items, setItems }) {
  const actualizarItem = (idx, campo, valor) => {
    setItems(arr => arr.map((it, i) => i === idx ? { ...it, [campo]: valor } : it));
  };
  const categorias = [...new Set(items.map(i => i.categoria))];
  return (
    <>
      <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginBottom: '.75rem' }}>
        Referencia: <b>B</b> = Bueno · <b>R</b> = Regular · <b>M</b> = Malo · <b>N/A</b> = No aplica
      </p>
      {categorias.map(categoria => (
        <div className="card" key={categoria} style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>{categoria}</p>
          {items.map((it, idx) => it.categoria === categoria && (
            <div key={idx} style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginBottom: '.5rem', flexWrap: 'wrap' }}>
              <span style={{ flex: '1 1 200px', fontSize: '.875rem' }}>{it.sistema}</span>
              <div style={{ display: 'flex', gap: '.25rem' }}>
                {ESTADOS.map(e => (
                  <button key={e.val} type="button"
                    className={it.estado === e.val ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                    style={{ padding: '.3rem .55rem', minWidth: 38 }}
                    onClick={() => actualizarItem(idx, 'estado', e.val)}>
                    {e.label}
                  </button>
                ))}
              </div>
              <input className="form-input" placeholder="Observación" style={{ flex: '2 1 180px', fontSize: '.8125rem' }}
                value={it.observacion || ''} onChange={e => actualizarItem(idx, 'observacion', e.target.value)} />
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

// ── Listado ───────────────────────────────────────────────────────

export default function RevisionGeneral() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [creando, setCreando] = useState(false);
  const [eliminandoId, setEliminandoId] = useState(null);
  const [busquedaLista, setBusquedaLista] = useState('');

  const cargar = () => {
    setLoading(true);
    api('/revisiones/').then(res => { setLista(res); setLoading(false); });
  };

  useEffect(() => { cargar(); }, []);

  const verDetalle = async (id) => {
    const res = await api(`/revisiones/${id}`);
    setDetalle(res);
  };

  const eliminar = async () => {
    await api(`/revisiones/${eliminandoId}`, { method: 'DELETE' });
    setEliminandoId(null);
    cargar();
  };

  const reabrirDesdeLista = async (id) => {
    await api(`/revisiones/${id}/reabrir`, { method: 'POST' });
    cargar();
  };

  const q = busquedaLista.trim().toLowerCase();
  const listaFiltrada = !q ? lista : lista.filter(c => (
    c.id.toLowerCase().includes(q) ||
    (c.nombre_cliente || '').toLowerCase().includes(q) ||
    (c.marca || '').toLowerCase().includes(q) ||
    (c.modelo || '').toLowerCase().includes(q) ||
    (c.matricula || '').toLowerCase().includes(q)
  ));

  if (creando) return (
    <FormularioRevision
      onCreado={(id) => { setCreando(false); cargar(); verDetalle(id); }}
      onCancelar={() => setCreando(false)}
    />
  );

  if (detalle) return (
    <DetalleRevision
      revision={detalle}
      onVolver={() => { setDetalle(null); cargar(); }}
      onActualizado={(d) => setDetalle(d)}
    />
  );

  const abiertas = listaFiltrada.filter(c => !c.cerrado);
  const cerradas = listaFiltrada.filter(c => !!c.cerrado);

  const tabla = (filas) => (
    <div className="tabla-wrap" style={{ marginBottom: '1rem' }}>
      <table className="tabla">
        <thead>
          <tr><th>ID</th><th>Fecha</th><th>Cliente</th><th>Vehículo</th><th>Estado</th><th></th></tr>
        </thead>
        <tbody>
          {filas.map(c => (
            <tr key={c.id}>
              <td><span className="badge badge-gris">{c.id}</span></td>
              <td>{fmtFecha(c.fecha)}</td>
              <td style={{ fontWeight: 500 }}>{c.nombre_cliente}</td>
              <td>{c.marca} {c.modelo} {c.matricula ? `| ${c.matricula}` : ''}</td>
              <td>
                {c.cerrado
                  ? <span className="badge badge-gris">Cerrada</span>
                  : <span className="badge badge-verde">Abierta</span>}
              </td>
              <td style={{ display: 'flex', gap: '.4rem' }}>
                <button className="btn btn-outline btn-sm" onClick={() => verDetalle(c.id)}>Ver</button>
                {!!c.cerrado && (
                  <button className="btn btn-outline btn-sm" onClick={() => reabrirDesdeLista(c.id)}>↺ Reabrir</button>
                )}
                <button className="btn btn-outline btn-sm" style={{ color: 'var(--rojo)' }}
                  onClick={() => setEliminandoId(c.id)}>🗑️</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="page-content">
      <div className="sec-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>Revisión general</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setCreando(true)}>+ Nueva revisión</button>
      </div>

      {lista.length > 0 && (
        <input className="form-input" placeholder="🔍 Buscar por cliente, marca, modelo, matrícula o N° de revisión..."
          value={busquedaLista} onChange={e => setBusquedaLista(e.target.value)}
          style={{ marginBottom: '.75rem' }} />
      )}

      {loading ? <Spinner texto="Cargando..." /> :
        lista.length === 0 ? <Empty icon="🔍" texto="Sin revisiones todavía" /> :
        listaFiltrada.length === 0 ? <Empty icon="🔍" texto="No hay revisiones que coincidan con la búsqueda" /> :
        <>
          {abiertas.length > 0 && (
            <>
              <p style={{ fontWeight: 600, color: 'var(--azul)', margin: '.25rem 0 .5rem' }}>Abiertas ({abiertas.length})</p>
              {tabla(abiertas)}
            </>
          )}
          {cerradas.length > 0 && (
            <>
              <p style={{ fontWeight: 600, color: 'var(--gris-5)', margin: '.25rem 0 .5rem' }}>Cerradas ({cerradas.length})</p>
              {tabla(cerradas)}
            </>
          )}
        </>
      }

      {eliminandoId && (
        <ConfirmModal
          mensaje="¿Eliminar esta revisión?"
          textoConfirmar="Eliminar"
          onConfirm={eliminar}
          onCancel={() => setEliminandoId(null)}
        />
      )}
    </div>
  );
}

// ── Nueva revisión ────────────────────────────────────────────────

function FormularioRevision({ onCreado, onCancelar }) {
  const [cliente, setCliente] = useState(null);
  const [modoNuevo, setModoNuevo] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState([]);

  const [vehiculosCliente, setVehiculosCliente] = useState([]);
  const [idVehiculo, setIdVehiculo] = useState(null);
  const [veh, setVeh] = useState(camposDeVehiculo(null));
  const [agregandoVeh, setAgregandoVeh] = useState(false);
  const [nuevoVeh, setNuevoVeh] = useState({ marca: '', modelo: '', anio: '', matricula: '' });

  const [fecha, setFecha] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Montevideo' }));
  const [km, setKm] = useState('');
  const [items, setItems] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api('/revisiones/items-base').then(setItems);
  }, []);

  useEffect(() => {
    if (busqueda.length < 2) { setResultados([]); return; }
    const t = setTimeout(async () => {
      const res = await api(`/clientes?q=${encodeURIComponent(busqueda)}`);
      const vistos = new Set();
      setResultados(res.filter(r => { if (vistos.has(r.id)) return false; vistos.add(r.id); return true; }));
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const elegirVehiculo = (v) => {
    setIdVehiculo(v.id);
    setVeh(camposDeVehiculo(v));
    setAgregandoVeh(false);
  };

  const seleccionarCliente = async (c) => {
    setCliente(c); setResultados([]); setBusqueda('');
    setIdVehiculo(null); setVeh(camposDeVehiculo(null)); setVehiculosCliente([]);
    try {
      const lista = await api(`/vehiculos/cliente/${c.id}`);
      setVehiculosCliente(lista);
      if (lista.length === 1) elegirVehiculo(lista[0]);
      if (lista.length === 0) setAgregandoVeh(true);
    } catch {
      setAgregandoVeh(true);
    }
  };

  const cambiarCliente = () => {
    setCliente(null); setVehiculosCliente([]); setIdVehiculo(null);
    setVeh(camposDeVehiculo(null)); setAgregandoVeh(false);
  };

  const agregarVehiculo = async () => {
    setError('');
    try {
      const res = await api('/vehiculos/', {
        method: 'POST',
        body: JSON.stringify({ id_cliente: cliente.id, ...nuevoVeh })
      });
      const v = { id: res.id, ...nuevoVeh };
      setVehiculosCliente(lista => [...lista, v]);
      setNuevoVeh({ marca: '', modelo: '', anio: '', matricula: '' });
      elegirVehiculo(v);
    } catch (e) {
      setError(e.message || 'No se pudo agregar el vehículo.');
    }
  };

  const guardar = async () => {
    if (!cliente) { setError('Elegí o creá un cliente primero.'); return; }
    if (!idVehiculo) { setError('Elegí el vehículo del cliente.'); return; }
    setGuardando(true); setError('');
    try {
      await api(`/vehiculos/${idVehiculo}`, { method: 'PATCH', body: JSON.stringify(veh) });
      const res = await api('/revisiones/', {
        method: 'POST',
        body: JSON.stringify({
          id_cliente: cliente.id,
          id_vehiculo: idVehiculo,
          fecha,
          kilometraje: km,
          observaciones_generales: observaciones,
          items,
        })
      });
      onCreado(res.id);
    } catch (e) {
      setError(e.message);
    }
    setGuardando(false);
  };

  return (
    <div className="page-content">
      <div className="sec-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>Nueva revisión general</h1>
        <button className="btn btn-secondary btn-sm" onClick={onCancelar}>← Cancelar</button>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Cliente</p>
        {modoNuevo && <NuevoClienteForm onCreado={(c, v) => {
          setCliente(c);
          setModoNuevo(false);
          if (v) {
            setVehiculosCliente([v]);
            elegirVehiculo(v);
          }
        }} onCancelar={() => setModoNuevo(false)} />}
        {!modoNuevo && !cliente && (
          <>
            <div className="sec-header" style={{ marginBottom: '.5rem' }}>
              <input className="form-input" placeholder="Buscar cliente..." value={busqueda}
                onChange={e => setBusqueda(e.target.value)} style={{ flex: 1 }} />
              <button className="btn btn-outline btn-sm" onClick={() => setModoNuevo(true)}>+ Nuevo</button>
            </div>
            {resultados.map(c => (
              <button key={c.id} className="btn btn-outline btn-sm" style={{ justifyContent: 'flex-start', width: '100%', marginBottom: '.3rem' }}
                onClick={() => seleccionarCliente(c)}>
                {c.nombre} {c.celular ? `— ${c.celular}` : ''}
              </button>
            ))}
          </>
        )}
        {!modoNuevo && cliente && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontWeight: 600 }}>{cliente.nombre}</p>
            <button className="btn btn-outline btn-sm" onClick={cambiarCliente}>Cambiar</button>
          </div>
        )}
      </div>

      {cliente && !modoNuevo && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="sec-header" style={{ marginBottom: '.6rem' }}>
            <p style={{ fontWeight: 600, color: 'var(--azul)' }}>Vehículo</p>
            {idVehiculo && (
              <button type="button" className="btn btn-outline btn-sm"
                onClick={() => { setIdVehiculo(null); setVeh(camposDeVehiculo(null)); }}>
                Cambiar vehículo
              </button>
            )}
          </div>

          {!idVehiculo && vehiculosCliente.length > 0 && (
            <div style={{ marginBottom: '.5rem' }}>
              <p style={{ fontSize: '.8125rem', marginBottom: '.4rem', color: 'var(--gris-5)' }}>Elegí el vehículo de la revisión:</p>
              {vehiculosCliente.map(v => (
                <button key={v.id} type="button" className="btn btn-outline btn-sm"
                  style={{ justifyContent: 'flex-start', width: '100%', marginBottom: '.3rem' }}
                  onClick={() => elegirVehiculo(v)}>
                  🚗 {etiquetaVehiculo(v)}
                </button>
              ))}
            </div>
          )}

          {!idVehiculo && !agregandoVeh && (
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setAgregandoVeh(true)}>
              + Agregar vehículo
            </button>
          )}

          {!idVehiculo && agregandoVeh && (
            <div style={{ marginTop: '.4rem' }}>
              <p style={{ fontSize: '.8125rem', marginBottom: '.4rem', color: 'var(--gris-5)' }}>
                {vehiculosCliente.length === 0 ? 'Este cliente todavía no tiene vehículos. Cargá el primero (el resto de los datos lo completás abajo):' : 'Nuevo vehículo (el resto de los datos lo completás abajo):'}
              </p>
              <div className="form-grid-2" style={{ rowGap: '.5rem', marginBottom: '.5rem' }}>
                <input className="form-input" placeholder="Marca" value={nuevoVeh.marca}
                  onChange={e => setNuevoVeh(v => ({ ...v, marca: e.target.value }))} />
                <input className="form-input" placeholder="Modelo" value={nuevoVeh.modelo}
                  onChange={e => setNuevoVeh(v => ({ ...v, modelo: e.target.value }))} />
                <input className="form-input" placeholder="Año" value={nuevoVeh.anio}
                  onChange={e => setNuevoVeh(v => ({ ...v, anio: e.target.value }))} />
                <input className="form-input" placeholder="Matrícula" value={nuevoVeh.matricula}
                  onChange={e => setNuevoVeh(v => ({ ...v, matricula: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: '.5rem' }}>
                <button type="button" className="btn btn-primary btn-sm" onClick={agregarVehiculo}>Agregar vehículo</button>
                {vehiculosCliente.length > 0 && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAgregandoVeh(false)}>Cancelar</button>
                )}
              </div>
            </div>
          )}

          {idVehiculo && (
            <>
              <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginBottom: '.5rem' }}>
                Es el vehículo del cliente: si corregís un dato acá, se actualiza también en su ficha.
              </p>
              <CamposVehiculo veh={veh} setVeh={setVeh} />
            </>
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="form-grid-2">
          <div>
            <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Fecha de la revisión</p>
            <input type="date" className="form-input" value={fecha} onChange={e => setFecha(e.target.value)} />
          </div>
          <div>
            <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Kilometraje</p>
            <input className="form-input" placeholder="Kilometraje" value={km} onChange={e => setKm(e.target.value)} />
          </div>
        </div>
      </div>

      <CargarFoto onItems={setItems} />

      <ListaItems items={items} setItems={setItems} />

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Observaciones generales</p>
        <textarea className="form-textarea" placeholder="Observaciones generales" rows={6}
          value={observaciones} onChange={e => setObservaciones(e.target.value)} />
      </div>

      {error && <p style={{ color: 'var(--rojo)', marginBottom: '.75rem', fontSize: '.875rem' }}>{error}</p>}
      {guardando && <Spinner texto="Guardando..." />}

      <button className="btn btn-primary btn-full" disabled={guardando} onClick={guardar}>
        ✅ Aceptar y guardar revisión
      </button>
    </div>
  );
}

// ── Editar ────────────────────────────────────────────────────────

function EditarRevision({ revision, onGuardado, onCancelar }) {
  const [cli, setCli] = useState({
    nombre: revision.nombre_cliente || '', celular: revision.celular || '',
    rut: revision.rut || '', direccion: revision.direccion || '',
  });
  const [veh, setVeh] = useState(camposDeVehiculo(revision));
  const [items, setItems] = useState(revision.items);
  const [observaciones, setObservaciones] = useState(revision.observaciones_generales || '');
  const [fecha, setFecha] = useState(revision.fecha || '');
  const [km, setKm] = useState(revision.kilometraje || '');
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api(`/clientes/${revision.id_cliente}`, {
        method: 'PATCH',
        body: JSON.stringify({
          nombre: cli.nombre, celular: cli.celular, rut: cli.rut, direccion: cli.direccion,
        })
      });
      if (revision.id_vehiculo) {
        await api(`/vehiculos/${revision.id_vehiculo}`, { method: 'PATCH', body: JSON.stringify(veh) });
      }
      await api(`/revisiones/${revision.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          id_cliente: revision.id_cliente,
          id_vehiculo: revision.id_vehiculo,
          fecha,
          kilometraje: km,
          observaciones_generales: observaciones,
          items,
        })
      });
      onGuardado();
    } catch {
      alert('No se pudo guardar.');
    }
    setGuardando(false);
  };

  return (
    <div className="page-content">
      <div className="sec-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>Editar {revision.id}</h1>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <a href={apiUrl(`/revisiones/${revision.id}/pdf-formulario`)} className="btn btn-outline btn-sm" target="_blank" rel="noreferrer">
            🖨️ Imprimir formulario en blanco
          </a>
          <button className="btn btn-secondary btn-sm" onClick={onCancelar}>← Cancelar</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="form-grid-2">
          <div>
            <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Fecha de la revisión</p>
            <input type="date" className="form-input" value={fecha} onChange={e => setFecha(e.target.value)} />
          </div>
          <div>
            <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Kilometraje</p>
            <input className="form-input" placeholder="Kilometraje" value={km} onChange={e => setKm(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Datos del cliente</p>
        <div className="form-grid-2" style={{ rowGap: '.5rem' }}>
          <input className="form-input" placeholder="Nombre completo" value={cli.nombre}
            onChange={e => setCli(c => ({ ...c, nombre: e.target.value }))} />
          <input className="form-input" placeholder="Celular" value={cli.celular}
            onChange={e => setCli(c => ({ ...c, celular: e.target.value }))} />
          <input className="form-input" placeholder="RUT o CI" value={cli.rut}
            onChange={e => setCli(c => ({ ...c, rut: e.target.value }))} />
          <input className="form-input" placeholder="Dirección" value={cli.direccion}
            onChange={e => setCli(c => ({ ...c, direccion: e.target.value }))} />
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Datos del vehículo</p>
        <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginBottom: '.5rem' }}>
          Si corregís un dato acá, se actualiza también en la ficha del cliente.
        </p>
        <CamposVehiculo veh={veh} setVeh={setVeh} />
      </div>

      <CargarFoto onItems={setItems} />

      <ListaItems items={items} setItems={setItems} />

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Observaciones generales</p>
        <textarea className="form-textarea" placeholder="Observaciones generales" rows={6}
          value={observaciones} onChange={e => setObservaciones(e.target.value)} />
      </div>

      {guardando && <Spinner texto="Guardando..." />}

      <button className="btn btn-primary btn-full" disabled={guardando} onClick={guardar}>
        ✅ Aceptar y guardar cambios
      </button>
    </div>
  );
}

// ── Detalle ───────────────────────────────────────────────────────

function DetalleRevision({ revision, onVolver, onActualizado }) {
  const navigate = useNavigate();
  const [editando, setEditando] = useState(false);
  const [armando, setArmando] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  const armarPresupuesto = async () => {
    setArmando(true);
    try {
      const res = await api(`/revisiones/${revision.id}/armar-presupuesto`, { method: 'POST' });
      navigate('/presupuestos', {
        state: {
          crear: true,
          clientePreseleccionado: res.cliente,
          vehiculoPreseleccionado: res.vehiculo,
          paquetesIniciales: res.paquetes.map(p => ({
            nombre: p.nombre, precio: 0,
            tareas: p.tareas,
            repuestos: [],
            costo_repuestos: 0, costo_mano_obra: 0, comision_ml_pct: 15, iva_pct: 22,
          })),
        }
      });
    } catch (e) {
      alert(e.message || 'No se pudo armar el presupuesto.');
    }
    setArmando(false);
  };

  const cambiarEstado = async (accion) => {
    setCambiandoEstado(true);
    try {
      await api(`/revisiones/${revision.id}/${accion}`, { method: 'POST' });
      const actualizado = await api(`/revisiones/${revision.id}`);
      onActualizado(actualizado);
    } catch {
      alert('No se pudo cambiar el estado de la revisión.');
    }
    setCambiandoEstado(false);
  };

  if (editando) {
    return (
      <EditarRevision
        revision={revision}
        onGuardado={async () => {
          const actualizado = await api(`/revisiones/${revision.id}`);
          setEditando(false);
          onActualizado(actualizado);
        }}
        onCancelar={() => setEditando(false)}
      />
    );
  }

  const categorias = [...new Set(revision.items.map(i => i.categoria))];
  const dato = (etiqueta, valor) => (
    <div className="resumen-card"><div className="label">{etiqueta}</div><div className="valor">{valor || '—'}</div></div>
  );

  return (
    <div className="page-content">
      <div className="sec-header" style={{ flexWrap: 'wrap', gap: '.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
          <h2 className="page-title" style={{ marginBottom: 0 }}>{revision.id}</h2>
          {revision.cerrado
            ? <span className="badge badge-gris">Cerrada</span>
            : <span className="badge badge-verde">Abierta</span>}
        </div>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={onVolver}>← Volver</button>
          <button className="btn btn-outline btn-sm" onClick={() => setEditando(true)}>✏️ Editar</button>
          <a href={apiUrl(`/revisiones/${revision.id}/pdf-formulario`)} className="btn btn-outline btn-sm" target="_blank" rel="noreferrer">
            🖨️ Formulario en blanco
          </a>
          <a href={apiUrl(`/revisiones/${revision.id}/pdf`)} className="btn btn-outline btn-sm" target="_blank" rel="noreferrer">
            📄 Ver PDF resultado
          </a>
          {revision.cerrado ? (
            <button className="btn btn-outline btn-sm" disabled={cambiandoEstado} onClick={() => cambiarEstado('reabrir')}>
              {cambiandoEstado ? '...' : '↺ Reabrir'}
            </button>
          ) : (
            <button className="btn btn-outline btn-sm" disabled={cambiandoEstado} onClick={() => cambiarEstado('cerrar')}>
              {cambiandoEstado ? '...' : '🔒 Cerrar revisión'}
            </button>
          )}
          <button className="btn btn-outline btn-sm" disabled={armando} onClick={armarPresupuesto}>
            {armando ? 'Leyendo observaciones...' : '📋 Armar presupuesto'}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="resumen-grid">
          {dato('Cliente', revision.nombre_cliente)}
          {dato('Celular', revision.celular)}
          {dato('RUT o CI', revision.rut)}
          {dato('Dirección', revision.direccion)}
          {dato('Fecha', fmtFecha(revision.fecha))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Datos del vehículo</p>
        <div className="resumen-grid">
          {dato('Marca', revision.marca)}
          {dato('Modelo', revision.modelo)}
          {dato('Año', revision.anio)}
          {dato('Color', revision.color)}
          {dato('Kilometraje', revision.kilometraje)}
          {dato('Combustible', revision.combustible)}
          {dato('Transmisión', revision.transmision)}
          {dato('Motor', revision.motor)}
          {dato('Admisión', revision.admision)}
          {dato('Matrícula', revision.matricula)}
          {dato('N° de chasis', revision.n_chasis)}
          {dato('N° de motor', revision.n_motor)}
        </div>
      </div>

      {categorias.map(categoria => (
        <div className="card" key={categoria} style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>{categoria}</p>
          {revision.items.filter(i => i.categoria === categoria).map((it, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8125rem',
                                    padding: '.3rem 0', borderBottom: '1px solid var(--gris-2)' }}>
              <span>{it.sistema}</span>
              <span style={{ fontWeight: 600 }}>{it.estado || '—'} {it.observacion ? `· ${it.observacion}` : ''}</span>
            </div>
          ))}
        </div>
      ))}

      {revision.observaciones_generales && (
        <div className="card">
          <p style={{ fontWeight: 600, color: 'var(--azul)', marginBottom: '.5rem' }}>Observaciones generales</p>
          <p style={{ fontSize: '.875rem', whiteSpace: 'pre-wrap' }}>{revision.observaciones_generales}</p>
        </div>
      )}
    </div>
  );
}
